import assetRequestFields from "config/forms/assetRequestFields";
import assetResponseFields from "config/forms/assetResponseFields";
import { parseDecimalValueToNumber } from "utils/decimalFieldHelpers";

const collectNumberFields = (fieldGroups) =>
  Object.values(fieldGroups).flatMap((fields) =>
    fields
      .filter((field) => field.type === "number")
      .map((field) => ({
        name: field.name,
        isDecimal: field.isDecimal,
        decimalPlaces: field.decimalPlaces,
      })),
  );

const assetNumberFields = Array.from(
  [
    ...collectNumberFields(assetRequestFields),
    ...collectNumberFields(assetResponseFields),
  ]
    .reduce((acc, field) => {
      if (!acc.has(field.name)) {
        acc.set(field.name, field);
      }

      return acc;
    }, new Map())
    .values(),
);

const cloneValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(cloneValue);
  }

  if (value && typeof value === "object") {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = cloneValue(value[key]);
      return acc;
    }, {});
  }

  return value;
};

const normalizeFormattedNumber = (value) => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmedValue = value.trim();

  if (trimmedValue.length === 0) {
    return value;
  }

  const normalizedValue = trimmedValue
    .replace(/\s/g, "")
    .replace(/\./g, "")
    .replace(/,/g, ".");

  const parsedValue = Number(normalizedValue);

  return Number.isNaN(parsedValue) ? value : parsedValue;
};

const normalizeFieldValue = (value, field) => {
  if (field.isDecimal) {
    const parsedValue = parseDecimalValueToNumber(value, {
      decimalPlaces: field.decimalPlaces,
    });

    return parsedValue === undefined ? value : parsedValue;
  }

  return normalizeFormattedNumber(value);
};

const hasPath = (data, pathParts) => {
  let currentValue = data;

  for (const part of pathParts) {
    if (
      currentValue === null ||
      currentValue === undefined ||
      typeof currentValue !== "object" ||
      !(part in currentValue)
    ) {
      return false;
    }

    currentValue = currentValue[part];
  }

  return true;
};

const getValueAtPath = (data, pathParts) => {
  return pathParts.reduce((acc, part) => acc?.[part], data);
};

const setValueAtPath = (data, pathParts, value) => {
  let currentValue = data;

  pathParts.slice(0, -1).forEach((part) => {
    currentValue = currentValue[part];
  });

  currentValue[pathParts[pathParts.length - 1]] = value;
};

export const normalizeAssetNumberFields = (assetPayload) => {
  const normalizedPayload = cloneValue(assetPayload);

  assetNumberFields.forEach((field) => {
    const pathParts = field.name.split(".");

    if (!hasPath(normalizedPayload, pathParts)) {
      return;
    }

    const currentValue = getValueAtPath(normalizedPayload, pathParts);
    const nextValue = normalizeFieldValue(currentValue, field);

    if (nextValue !== currentValue) {
      setValueAtPath(normalizedPayload, pathParts, nextValue);
    }
  });

  return normalizedPayload;
};

export default normalizeAssetNumberFields;
